// FORM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/containers/FieldRenderers/TemplateFieldRenderers/CheckboxGroup/index.tsx

import { Toast } from '@modusign/design-system';
import { useCheckboxFieldGroup } from '@modusign/react-signing';
import { CheckboxField } from '@modusign/react-signing/types';
import { useCallback, useState } from 'react';

import { FIELD_MAX_COUNT } from '@app/constants/field';
import calculateRelativeMaxConstraint from '@app/containers/FieldRenderers/libs/checkboxGroup/calculateRelativeMaxConstraint';
import ConfigurableCheckboxGroup from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/ConfigurableCheckboxGroup';
import { ConfigurableCheckboxFieldRenderer } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfigurableCheckboxGroupFieldRenderer/ConfigurableCheckboxFieldRenderer';
import { ReactSigningFieldRendererProps } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';

export default function ConfigurableCheckboxGroupFieldRenderer({
  fieldId,
  readOnly,
  restrictModification,
  scale,
  viewport,
  page,
}: ReactSigningFieldRendererProps) {
  const {
    checkboxGroup,
    boundingBox, // TODO: (Juyeong-Byeon) Rect 타입 {position, size}로 수정
    addChildCheckbox,
    removeChildCheckbox,
    setDataLabel,
    setRequired,
    setCheckboxGroupMode,
    updateIndices,
  } = useCheckboxFieldGroup(fieldId);

  const {
    evaluated: { isDataLabelDuplicated, totalFieldCount },
    removeField,
  } = useOptimalFieldsContext<'CHECKBOX_GROUP', 'SIGNER'>();

  const [shouldContextMenuOpen, setIsContextMenuOpen] = useState(true);
  const [isAdjustHandling, setIsAdjustHandling] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleChildPositionChangeStart = useCallback(() => {
    setIsAdjustHandling(true);
    setIsContextMenuOpen(true);
  }, []);

  const handleOutsideClick = useCallback(() => {
    if (!checkboxGroup?.dataLabel || isDataLabelDuplicated || !shouldContextMenuOpen) {
      return;
    }

    if (!isAdjustHandling) {
      setIsContextMenuOpen(false);
    }

    if (restrictModification) {
      setIsAdjustHandling(false);
    }
  }, [
    checkboxGroup?.dataLabel,
    isAdjustHandling,
    isDataLabelDuplicated,
    restrictModification,
    shouldContextMenuOpen,
  ]);

  const handleCheckBoxCountChange = (targetCount: number) => {
    if (!checkboxGroup) return;

    const diff = targetCount - checkboxGroup.children.length;
    if (totalFieldCount + diff > FIELD_MAX_COUNT) {
      Toast.warning({
        title: '입력란 추가 실패',
        description: `한 문서에 최대 ${FIELD_MAX_COUNT.toLocaleString(
          'ko-KR'
        )}개까지의 입력란을 추가할 수 있습니다. (현재 ${totalFieldCount.toLocaleString(
          'ko-KR'
        )}개)`,
      });
      return;
    }
    for (let i = 0; i < Math.abs(diff); i += 1) {
      if (diff > 0) {
        addChildCheckbox();
      } else {
        removeChildCheckbox();
      }
    }

    updateIndices();
  };

  if (!checkboxGroup || checkboxGroup.type !== 'CHECKBOX_GROUP') return null;

  const participantOrder =
    checkboxGroup.participantReference.type === 'SIGNER'
      ? checkboxGroup.participantReference.index + 1
      : 1;

  return (
    <ConfigurableCheckboxGroup
      boundingBox={boundingBox}
      contextMenu={{
        count: checkboxGroup.children.length,
        required: checkboxGroup.config.required,
        mode: checkboxGroup.config.mode,
        dataLabel: checkboxGroup.dataLabel,
        isDataLabelDuplicated,
      }}
      isContextMenuOpen={shouldContextMenuOpen}
      key={fieldId}
      participantName={checkboxGroup.participantReference.name || ''}
      participantOrder={participantOrder}
      participantRole={
        checkboxGroup.participantReference.type === 'SIGNER'
          ? checkboxGroup.participantReference.role
          : ''
      }
      readOnly={readOnly}
      restrictModification={restrictModification}
      shouldDrawBorder={isHovered || isAdjustHandling}
      viewport={viewport}
      onCheckBoxCountChange={handleCheckBoxCountChange}
      onCheckBoxGroupModeChange={(mode) => setCheckboxGroupMode(mode)}
      onDataLabelChange={(dataLabel) => setDataLabel(dataLabel)}
      onDelete={() => removeField(fieldId)}
      onOutsideClick={handleOutsideClick}
      onRequiredChange={(isRequired) => setRequired(isRequired)}
    >
      {checkboxGroup.children
        .filter((child): child is CheckboxField => child.type === 'CHECKBOX')

        .map((child) => {
          return (
            <ConfigurableCheckboxFieldRenderer
              fieldId={child.id}
              key={child.id}
              maxConstraint={calculateRelativeMaxConstraint(
                boundingBox,
                child.size.width,
                child.size.height
              )}
              page={page}
              readOnly={readOnly}
              restrictModification={restrictModification}
              scale={scale}
              viewport={viewport}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onPositionChangeEnd={() => setIsAdjustHandling(false)}
              onPositionChangeStart={handleChildPositionChangeStart}
              onSizeChangeEnd={() => setIsAdjustHandling(false)}
              onSizeChangeStart={() => setIsAdjustHandling(true)}
            />
          );
        })}
    </ConfigurableCheckboxGroup>
  );
}
